import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { AI_ASSET_GENERATOR_HELPER, getBaseURL } from "../../../../env";

// JS port of portal's usePublicLibraryImages.ts (hooks/usePublicLibraryImages.ts).
// Hits the same `ai-asset-generator-helper` lambda with action
// `get_ai_image_public_library` — populates the ALL3D Library tab in the
// hero's inspiration modal with the same explore-section feed used by the
// portal CreateProductModal.

const PAGE_SIZE = 50;

const baseURL = () => {
    try {
        return getBaseURL("aws");
    } catch {
        return "";
    }
};

const mapPublicLibraryImages = (rows) => {
    const data = (Array.isArray(rows) ? rows : []).filter((row) => row && row.uri);
    return data.map((row) => {
        const uri = row.uri || "";
        const isVideo = row.image_type === "ai_video";
        const thumb = (row.thumbnail || uri).replace(/(\.[^.]+)$/, "_512$1");
        const srcPath = isVideo ? uri : uri.replace(/(\.[^.]+)$/, "_compressed.webp");
        const base = uri.startsWith("http") ? "" : baseURL();

        return {
            src: uri.startsWith("http")
                ? (isVideo ? uri : uri.replace(/(\.[^.]+)$/, "_compressed.webp"))
                : base + srcPath,
            thumbnail: thumb.startsWith("http") ? thumb : base + thumb,
            videoUrl: isVideo ? (uri.startsWith("http") ? uri : base + uri) : undefined,
            originalUrl: uri.startsWith("http") ? uri : base + uri,
            display_name: row.display_name || "",
            product_category: row.product_category || "",
            id: row.id || "",
            render_id: row.render_id || "",
            image_type: row.image_type || "ai",
            image_width: row.width,
            image_height: row.height,
            inspiration_id: row.inspiration_id,
        };
    });
};

export const useHeroPublicLibraryImages = (enabled) => {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const offsetRef = useRef(0);
    const hasFetchedRef = useRef(false);

    const fetchPage = useCallback(async (offset, isInitial) => {
        if (isInitial) setLoading(true);
        else setLoadingMore(true);

        try {
            const res = await axios.post(AI_ASSET_GENERATOR_HELPER, {
                action: "get_ai_image_public_library",
                images_only: true,
                limit: PAGE_SIZE,
                offset,
            });
            const rows = res.data?.data || res.data || [];
            const mapped = mapPublicLibraryImages(rows);

            if (isInitial) setImages(mapped);
            else setImages((prev) => [...prev, ...mapped]);

            if (!Array.isArray(rows) || rows.length < PAGE_SIZE) setHasMore(false);
            offsetRef.current = offset + (Array.isArray(rows) ? rows.length : 0);
        } catch (err) {
            console.error("ALL3D Library fetch failed:", err);
        } finally {
            if (isInitial) setLoading(false);
            else setLoadingMore(false);
        }
    }, []);

    useEffect(() => {
        if (!enabled || hasFetchedRef.current) return;
        hasFetchedRef.current = true;
        offsetRef.current = 0;
        setHasMore(true);
        fetchPage(0, true);
    }, [enabled, fetchPage]);

    const loadMore = useCallback(() => {
        if (loadingMore || !hasMore) return;
        fetchPage(offsetRef.current, false);
    }, [loadingMore, hasMore, fetchPage]);

    return { images, loading, loadingMore, hasMore, loadMore };
};
